"use client"

import { useState, useEffect, useCallback, useMemo } from "react"
import { Search, Loader2 } from "lucide-react"
import { motion } from "framer-motion"
import Skeleton from "@mui/material/Skeleton"
import { TemplateSlideCard } from "./template-slide-card"
import { TemplatePreviewModal } from "./template-preview-modal"
import { 
  useLazyGetTemplatesQuery, 
  useCreateUserTemplateMutation,
  useDeleteUserTemplateMutation,
  type TemplateItem 
} from "@/app/store/api/templateApi"
import { useDebounce } from "@/hooks/use-debounce"
import { cn } from "@/lib/utils"
import { DocumentTemplates } from "./document-templates"
import { DocumentPreviewModal } from "./document-preview-modal"
import { UploadCustomTemplateModal } from "@/components/modal-dialogs/upload-custom-template-modal/upload-custom-template-modal"
import { Input } from "@/components/ui/input"
import type { DeckType } from "../home/deck-type-chips"
import { useAppSelector } from "@/app/store/hooks"

const PAGE_SIZE = 4
interface TemplateGalleryProps {
  limit?: number
  showSearch?: boolean
  onUseTemplate?: (template: TemplateItem) => void
  selectedDeckType?: DeckType
  selectedDocumentSubtype?: string | null
}

function LoadingState({ count = 4 }: { count?: number }) {
  return (
    <div className="grid grid-cols-2 gap-3 md:gap-6 lg:grid-cols-4">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="flex flex-col rounded-2xl border border-slate-100 bg-white shadow-sm overflow-hidden">
          <Skeleton variant="rectangular" height={128} className="w-full" animation="wave" />
          <div className="p-4 space-y-3">
            <Skeleton variant="text" height={20} width="70%" animation="wave" />
          </div>
        </div>
      ))}
    </div>
  )
}

export function TemplateGallery({ 
  limit, 
  showSearch = false, 
  onUseTemplate, 
  selectedDeckType = "Presentation",
  selectedDocumentSubtype = null
}: TemplateGalleryProps) {
  const [searchQuery, setSearchQuery] = useState("")
  const [previewTemplate, setPreviewTemplate] = useState<TemplateItem | null>(null)
  const [previewDocument, setPreviewDocument] = useState<any | null>(null)
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)
  
  // Consolidated state for templates
  const [templates, setTemplates] = useState<TemplateItem[]>([])
  const [hasMore, setHasMore] = useState(false)
  const [offset, setOffset] = useState(0)
  const [isInitialLoad, setIsInitialLoad] = useState(true)

  const { isAuthenticated, token } = useAppSelector((state) => state.auth)
  const isLoggedIn = !!(token && isAuthenticated)

  const debouncedSearch = useDebounce(searchQuery, 300)
  const pageSize = limit || PAGE_SIZE

  const [fetchTemplates, { isFetching }] = useLazyGetTemplatesQuery()
  const [createUserTemplate] = useCreateUserTemplateMutation()
  const [deleteUserTemplate] = useDeleteUserTemplateMutation()

  const loadTemplates = useCallback(async (currentOffset: number, reset: boolean, search?: string) => {
    try {
      const result = await fetchTemplates({
        offset: currentOffset,
        limit: 100,
        search: search || undefined,
        documentType: selectedDocumentSubtype || undefined,
        isAuthenticated: isLoggedIn,
      }).unwrap()

      const newTemplates = result?.data?.templates || []
      const pagination = result?.data?.pagination

      setTemplates(prev => {
        const base = reset ? [] : prev
        const existingIds = new Set(base.map(t => t.templateId || t._id))
        const unique = newTemplates.filter(t => !existingIds.has(t.templateId || t._id))
        return [...base, ...unique]
      })

      if (reset) {
        setOffset(newTemplates.length)
      } else {
        setOffset(currentOffset + newTemplates.length)
      }
      
      setHasMore(pagination?.hasMore ?? false)
      setIsInitialLoad(false)
    } catch (error) {
      console.error(`Failed to fetch templates:`, error)
      setIsInitialLoad(false)
    }
  }, [fetchTemplates, selectedDocumentSubtype])

  // Initial load
  useEffect(() => {
    if (isInitialLoad) {
      loadTemplates(0, true, debouncedSearch)
    }
  }, [loadTemplates, isInitialLoad, debouncedSearch])

  // Reset when search changes
  useEffect(() => {
    loadTemplates(0, true, debouncedSearch)
  }, [debouncedSearch]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleLoadMore = () => {
    loadTemplates(offset, false, debouncedSearch)
  }

  const handleDeleteTemplate = async (templateId: string) => {
    try {
      await deleteUserTemplate(templateId).unwrap()
      setTemplates(prev => prev.filter(t => (t.templateId || t._id) !== templateId))
    } catch (error) {
      console.error("Failed to delete template:", error)
    }
  }

  // Filter templates based on selectedDeckType and selectedDocumentSubtype
  const filteredTemplates: TemplateItem[] = useMemo(() => {
      const isDocumentView = selectedDeckType === "documents" || selectedDeckType === "deep-research"
      let result: TemplateItem[] = []
      
      if (isDocumentView) {
          result = templates.filter(t => {
              // Documents must have a docxUrl (top-level or in documentTemplate)
              const hasDocx = !!(t.docxUrl || t.documentTemplate?.docxUrl);
              if (!hasDocx) return false;
              
              if (!selectedDocumentSubtype) return true;
              
              // Filter by category matching subtype (normalize to lowercase for comparison)
              const categoryMatch = t.category?.toLowerCase() === selectedDocumentSubtype.toLowerCase();
              return categoryMatch;
          });
      } else {
          // Presentation or Fundraising - only show templates without docxUrl
          result = templates.filter(t => {
              const hasDocx = !!(t.docxUrl || t.documentTemplate?.docxUrl);
              return !hasDocx;
          });
      }

      // Final safety deduplication
      const seen = new Set();
      return result.filter((t, idx) => {
          const id = t.templateId || t._id || `fallback-${idx}`;
          if (seen.has(id)) return false;
          seen.add(id);
          return true;
      });
  }, [templates, selectedDeckType, selectedDocumentSubtype])

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <h2 className="text-xl md:text-2xl text-slate-400 font-medium leading-none">
            Start with a <span className="font-bold text-slate-800">Template</span>
          </h2>

          {showSearch && (
            <div className="relative w-full md:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type="text"
                value={searchQuery}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
                placeholder="Search templates..."
                className="pl-9 h-10 text-xs bg-slate-100 border-0 shadow-none focus-visible:ring-1 focus-visible:ring-indigo-500/20 rounded-2xl transition-all hover:bg-slate-200/50 w-full"
              />
            </div>
          )}
        </div>

        {/* Dynamic Title / Selection indicator */}
        {/* <div className="relative border-b border-slate-200 w-full">
          <div className="pb-4">
            <span className="text-sm font-bold text-slate-800 capitalize">
              {selectedDeckType === "documents" || selectedDeckType === "deep-research" ? "Document Templates" : "Presentation Templates"}
            </span>
          </div>
        </div> */}
      </div>

      {/* Grid */}
      {selectedDeckType === "documents" || selectedDeckType === "deep-research" ? (
        <DocumentTemplates 
          templates={filteredTemplates}
          onUseTemplate={onUseTemplate} 
          onPreview={(doc) => setPreviewDocument(doc)}
          showUpload={selectedDocumentSubtype === "custom"}
          onUploadClick={() => setIsUploadModalOpen(true)}
          onDelete={handleDeleteTemplate}
        />
      ) : isInitialLoad ? (
        <LoadingState count={pageSize} />
      ) : filteredTemplates.length === 0 ? (
        <div className="py-12 text-center">
          <p className="text-sm text-slate-400">
            {debouncedSearch ? "No templates found for your search." : "No templates available yet."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 md:gap-3.5 lg:grid-cols-4">
          {filteredTemplates.map((template: TemplateItem, index: number) => (
            <TemplateSlideCard
              key={`${template.templateId || template._id}-${index}`}
              template={template}
              onClick={() => {
                if (template.documentTemplate) {
                  setPreviewDocument({
                    ...template,
                    pages: (template as any).pages || [{ id: "p1", thumbnailUrl: template.documentTemplate.thumbnailUrl, name: "Page 1" }]
                  })
                } else {
                  setPreviewTemplate(template)
                }
              }}
              onUseTemplate={() => onUseTemplate?.(template)}
            />
          ))}
        </div>
      )}

      {/* Load More */}
      {hasMore && !isInitialLoad && (
        <div className="flex justify-center pt-4 pb-2">
          <button
            onClick={handleLoadMore}
            disabled={isFetching}
            className="px-6 py-2.5 cursor-pointer rounded-full bg-white border border-slate-200 text-sm font-medium text-slate-600 shadow-sm hover:bg-slate-50 hover:text-slate-900 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isFetching ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading...
              </>
            ) : (
              "Load More"
            )}
          </button>
        </div>
      )}

      {/* Preview modals */}
      <TemplatePreviewModal
        template={previewTemplate}
        isOpen={!!previewTemplate}
        onClose={() => setPreviewTemplate(null)}
        onUseTemplate={(template) => {
          setPreviewTemplate(null)
          onUseTemplate?.(template)
        }}
      />

      <DocumentPreviewModal
        document={previewDocument}
        isOpen={!!previewDocument}
        onClose={() => setPreviewDocument(null)}
        onUseTemplate={(doc) => {
          setPreviewDocument(null)
          onUseTemplate?.(doc as any)
        }}
      />

      <UploadCustomTemplateModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onUpload={async ({ name, file }) => {
          try {
            const newTemplate = await createUserTemplate({
              name,
              file,
              documentType: selectedDocumentSubtype || "custom"
            }).unwrap()
            
            setTemplates(prev => [newTemplate, ...prev])
            onUseTemplate?.(newTemplate)
            setIsUploadModalOpen(false)
          } catch (error) {
            console.error("Upload failed:", error)
            throw error // Re-throw to show error in modal
          }
        }}
      />
    </div>
  )
}
